import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Music, Users, TrendingUp, Award, Zap, Shield, MessageSquare, DollarSign, CheckCircle, Star, Upload, Radio, ListMusic, Headphones, Gift, Target, ExternalLink } from "lucide-react";
import deadWordsLogo from "@/assets/dead-words-logo.png";
import deadAirLogo from "@/assets/dead-air-logo.png";
import deadMastersLogo from "@/assets/dead-masters-logo.png";
import { Button } from "@/components/ui/button";
import { digitalOcean } from "@/integrations/digitalocean/client";

export default function Home() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await digitalOcean.auth.getSession();
        setIsLoggedIn(!!session);
      } catch (error) {
        console.error("Auth check error:", error);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    document.title = "Soundope | Real Feedback, Organic Growth & Get Paid to Discover Music";
    
    const previewImageUrl = "https://base44-prod.nyc3.cdn.digitaloceanspaces.com/public/6904b6d46257f14ce2e4440b/df45ac3bc_Screenshot2025-11-01at70542PM.png";
    
    const metaTags = [
      { type: 'name', key: 'description', content: "The all-in-one tool for independent artists. Real feedback, organic growth, free playlist submissions. Artists pay less, users get paid more. Get paid to discover new music!" },
      { type: 'name', key: 'keywords', content: "get paid to review music, music feedback platform, organic artist growth, free playlist submission, independent artist tools, earn money reviewing music, honest music reviews, submithub alternative, playlist push alternative, music promotion for artists" },
      { type: 'property', key: 'og:title', content: 'Soundope | Real Feedback, Organic Growth & Get Paid to Discover Music' },
      { type: 'property', key: 'og:description', content: 'Artists pay less. Users get paid more. Get paid to discover new music!' },
      { type: 'property', key: 'og:image', content: previewImageUrl },
      { type: 'property', key: 'og:url', content: window.location.href },
      { type: 'property', key: 'og:type', content: 'website' },
      { type: 'name', key: 'twitter:card', content: 'summary_large_image' },
      { type: 'name', key: 'twitter:title', content: 'Soundope | Get Paid to Discover New Music' },
      { type: 'name', key: 'twitter:description', content: 'Artists pay less. Users get paid more. The all-in-one tool for independent artists.' },
      { type: 'name', key: 'twitter:image', content: previewImageUrl },
      { type: 'name', key: 'robots', content: 'index, follow' },
    ];

    metaTags.forEach(({ type, key, content }) => {
      let tag = document.querySelector(`meta[${type}="${key}"]`);
      if (tag) {
        tag.setAttribute('content', content);
      } else {
        tag = document.createElement('meta');
        tag.setAttribute(type, key);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
      }
    });

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "name": "Soundope",
          "url": window.location.origin,
          "description": "The all-in-one tool for independent artists. Real feedback, organic growth, free playlist submissions."
        },
        {
          "@type": "Organization",
          "name": "Soundope",
          "url": window.location.origin,
          "logo": previewImageUrl
        }
      ]
    });
    
    const oldSchema = document.querySelector('script[type="application/ld+json"]');
    if (oldSchema) oldSchema.remove();
    document.head.appendChild(schemaScript);

    return () => {
      if (schemaScript.parentNode) schemaScript.parentNode.removeChild(schemaScript);
    };
  }, []);

  const handleGetStarted = () => navigate("/auth?mode=signup");
  const handleUpload = () => navigate("/upload");
  const handleGrowth = () => navigate("/growth-program");

  return (
    <div className="min-h-screen px-4 py-8 md:py-16 pb-32 md:pb-16 flex justify-center">
      <div className="max-w-4xl w-full mx-auto">
        
        {/* MAIN HEADLINE */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl lg:text-6xl text-[#e0e0e0] mb-6 font-masking-renta leading-tight">
            Soundope: The All-in-One Tool for Independent Artists
          </h1>
          <p className="text-xl md:text-2xl text-[#c0a060] mb-4 font-medium">
            Real Feedback, Playlist Pitching, and a Variety of Tools Artists Use Everyday
          </p>
          <p className="text-lg md:text-xl text-[#90b090] mb-2">
            Artists pay less. Users get paid more.
          </p>
        </div>

        {/* GET PAID HERO SECTION */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12 border-2 border-[#c0a060]/40 bg-gradient-to-br from-[#0a0a0a] to-[#151510]">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <DollarSign className="w-10 h-10 text-[#c0a060]" />
              <h2 className="text-3xl md:text-5xl text-[#c0a060] font-creepy-zombie uppercase tracking-wide">
                GET PAID TO DISCOVER NEW MUSIC!
              </h2>
            </div>
            
            <h3 className="text-2xl md:text-3xl text-[#d0d0d0] mb-6 font-masking-renta">
              Get Paid to Review Music
            </h3>
            
            <p className="text-base md:text-lg text-[#a0a0a0] max-w-3xl mx-auto mb-8 leading-relaxed">
              Earn real money by leaving honest, detailed feedback on other artists' tracks. 
              The more helpful and in-depth your reviews, the more you earn – <span className="text-[#c0a060] font-medium">cash out anytime via PayPal</span>. 
              Turn your music expertise into extra income while supporting the community!
            </p>
            
            <Button
              onClick={handleGetStarted}
              className="neuro-base active:neuro-pressed rounded-3xl px-10 py-6 text-xl smooth-transition hover:scale-105 border-2 border-[#c0a060]/50"
            >
              <DollarSign className="w-6 h-6 mr-2 text-[#c0a060]" />
              <span className="text-[#c0a060]">Start Earning Now</span>
            </Button>
          </div>
        </div>

        {/* ELEVATE YOUR CAREER SECTION */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12 border border-[#252525] bg-[#0a0a0a]">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl text-[#d0d0d0] mb-6 font-masking-renta leading-tight">
              Elevate Your Music Career with Honest Feedback, Free Playlist Submissions, and Proven Growth Strategies
            </h2>
            
            <p className="text-base md:text-lg text-[#909090] max-w-3xl mx-auto mb-6 leading-relaxed">
              Are you an independent artist tired of fake streams, bot-driven engagement, and sugarcoated reviews? 
              <span className="text-[#b0b0b0] font-medium"> Soundope is the ultimate all-in-one platform built by artists, for artists</span> – 
              delivering real music feedback, free playlist submissions, unbiased music reviews, and organic growth tools to help you thrive in the underground.
            </p>
            
            <p className="text-xl md:text-2xl text-[#90b090] font-creepy-zombie mb-8">
              Get heard. Be absurd. Make dope sounds with Soundope.
            </p>
            
            <p className="text-sm text-[#606060] mb-6">Start Free Today – No Credit Card Required</p>
            
            <Button
              onClick={handleUpload}
              className="neuro-base active:neuro-pressed rounded-3xl px-10 py-6 text-xl smooth-transition hover:scale-105 border-2 border-[#90b090]/50"
            >
              <Upload className="w-6 h-6 mr-2 text-[#90b090]" />
              <span className="text-[#90b090]">Upload Your First Track Free</span>
            </Button>
          </div>
        </div>

        {/* WHY CHOOSE SOUNDOPE */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12 border border-[#252525] bg-[#0a0a0a]">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl text-[#d0d0d0] mb-4 font-masking-renta">
              Why Choose Soundope as Your All-in-One Artist Growth Tool?
            </h2>
            <p className="text-base md:text-lg text-[#909090] max-w-3xl mx-auto leading-relaxed">
              In a world full of AI bots and paid fake promo, Soundope stands out with <span className="text-[#90b0d0] font-medium">100% human-powered interactions</span>. 
              We focus on genuine connections, actionable insights, and sustainable growth – no shortcuts, no lame shit.
            </p>
          </div>

          {/* FEATURE CARDS */}
          <div className="space-y-8">
            
            {/* Free Music Reviews */}
            <div className="neuro-pressed rounded-2xl p-6 md:p-8 border border-[#90b090]/30">
              <div className="flex items-start gap-4">
                <MessageSquare className="w-10 h-10 text-[#90b090] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl text-[#90b090] mb-3 font-masking-renta">
                    Free Music Reviews & Honest Feedback
                  </h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Upload your tracks to our discovery page and get constructive reviews from real artists and listeners. 
                    We cover production quality, lyrics, mix, vibe, and marketability. No trolling, no fluff – just honest insights to level up your craft. 
                    <span className="text-[#90b090] font-medium"> Feedback forever, completely free.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Free Playlist Submissions */}
            <div className="neuro-pressed rounded-2xl p-6 md:p-8 border border-[#9090b0]/30">
              <div className="flex items-start gap-4">
                <ListMusic className="w-10 h-10 text-[#9090b0] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl text-[#9090b0] mb-3 font-masking-renta">
                    Free Playlist Submissions & Paid Fast-Lane Options
                  </h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Every upload automatically gets considered for our in-house, vetted playlists – at no cost. 
                    Want faster access to niche curators? Our paid submissions are <span className="text-[#9090b0] font-medium">half the price of competitors</span> (like SubmitHub or Playlist Push). 
                    Real taste-based placements only – no guarantees, just quality.
                  </p>
                </div>
              </div>
            </div>

            {/* PRO Growth Packages */}
            <div className="neuro-pressed rounded-2xl p-6 md:p-8 border border-[#c0a060]/30">
              <div className="flex items-start gap-4">
                <TrendingUp className="w-10 h-10 text-[#c0a060] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl text-[#c0a060] mb-3 font-masking-renta">
                    Exclusive PRO Growth Packages
                  </h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Our Guaranteed Growth Packages combine expert feedback with tailored organic strategies for real results (or your money back). 
                    Build Spotify listeners without ads, boost TikTok/Instagram reels and engagement, and spark authentic cross-promos and collabs. 
                    Track progress, iterate, and watch your audience grow naturally.
                  </p>
                </div>
              </div>
            </div>

            {/* Dead Air Crossposting */}
            <div className="neuro-pressed rounded-2xl p-6 md:p-8 border border-[#90b0d0]/30">
              <div className="flex items-start gap-4">
                <Radio className="w-10 h-10 text-[#90b0d0] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl text-[#90b0d0] mb-3 font-masking-renta">
                    Exclusive Dead Air Crossposting App
                  </h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    Spend more time creating music and less time posting on social media. 
                    Our Dead Air app automates crossposting across platforms, handling your promo efficiently so you can focus on what matters: <span className="text-[#90b0d0] font-medium">making fire tracks</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* Social Media Growth */}
            <div className="neuro-pressed rounded-2xl p-6 md:p-8 border border-[#b06080]/30">
              <div className="flex items-start gap-4">
                <Users className="w-10 h-10 text-[#b06080] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl md:text-2xl text-[#b06080] mb-3 font-masking-renta">
                    Social Media Growth & Promo Help for Artists
                  </h3>
                  <p className="text-[#a0a0a0] leading-relaxed">
                    From community-driven shares to tips on building real fans, Soundope helps you grow on socials organically. 
                    Earn money by reviewing others' tracks (paid via PayPal), become a curator, and tap into a supportive underground network.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* PREMIUM ARTIST TOOLS SECTION */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12 border border-[#252525] bg-[#0a0a0a]">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-4xl text-[#d0d0d0] mb-4 font-masking-renta">
              Your Complete Artist Toolkit
            </h2>
            <p className="text-base md:text-lg text-[#909090] max-w-3xl mx-auto leading-relaxed">
              Save hours of work with our premium tools. Focus on making music while we handle the rest.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            
            {/* Dead Words */}
            <a
              href="https://deadwords.us"
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div 
                className="neuro-pressed rounded-2xl p-6 border border-[#a855f7]/30 h-full transition-all duration-300 hover:scale-[1.02] hover:border-[#a855f7]/60"
                style={{ boxShadow: `0 0 0 rgba(0,0,0,0)` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 30px rgba(168, 85, 247, 0.3)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 rgba(0,0,0,0)`; }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={deadWordsLogo} alt="Dead Words" className="w-12 h-12 object-contain" />
                  <h3 className="text-xl text-[#a855f7] font-masking-renta">Dead Words</h3>
                </div>
                <h4 className="text-lg text-[#d0d0d0] mb-2 font-medium group-hover:text-white transition-colors">
                  Lyric Videos in Minutes
                </h4>
                <p className="text-sm text-[#909090] mb-4 leading-relaxed">
                  Generate perfectly-timed lyric videos with slideshow or video clips. No editing skills required.
                </p>
                <div className="flex items-center gap-2 text-[#707070] group-hover:text-[#a855f7] transition-colors text-sm">
                  <span>deadwords.us</span>
                  <ExternalLink className="w-3 h-3" />
                </div>
              </div>
            </a>

            {/* Dead Masters */}
            <a
              href="https://deadmasters.us"
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div 
                className="neuro-pressed rounded-2xl p-6 border border-[#c0a060]/30 h-full transition-all duration-300 hover:scale-[1.02] hover:border-[#c0a060]/60"
                style={{ boxShadow: `0 0 0 rgba(0,0,0,0)` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 30px rgba(192, 160, 96, 0.3)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 rgba(0,0,0,0)`; }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={deadMastersLogo} alt="Dead Masters" className="w-12 h-12 object-contain" />
                  <h3 className="text-xl text-[#c0a060] font-masking-renta">Dead Masters</h3>
                </div>
                <h4 className="text-lg text-[#d0d0d0] mb-2 font-medium group-hover:text-white transition-colors">
                  Professional Mastering
                </h4>
                <p className="text-sm text-[#909090] mb-4 leading-relaxed">
                  Master your tracks professionally in minutes. Studio-quality sound without the studio prices. Enough said.
                </p>
                <div className="flex items-center gap-2 text-[#707070] group-hover:text-[#c0a060] transition-colors text-sm">
                  <span>deadmasters.us</span>
                  <ExternalLink className="w-3 h-3" />
                </div>
              </div>
            </a>

            {/* Dead Air */}
            <a
              href="https://deadair.us"
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div 
                className="neuro-pressed rounded-2xl p-6 border border-[#22d3ee]/30 h-full transition-all duration-300 hover:scale-[1.02] hover:border-[#22d3ee]/60"
                style={{ boxShadow: `0 0 0 rgba(0,0,0,0)` }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 30px rgba(34, 211, 238, 0.3)`; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 rgba(0,0,0,0)`; }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <img src={deadAirLogo} alt="Dead Air" className="w-12 h-12 object-contain" />
                  <h3 className="text-xl text-[#22d3ee] font-masking-renta">Dead Air</h3>
                </div>
                <h4 className="text-lg text-[#d0d0d0] mb-2 font-medium group-hover:text-white transition-colors">
                  Crosspost Everywhere
                </h4>
                <p className="text-sm text-[#909090] mb-4 leading-relaxed">
                  Post to ALL your socials with 1 click. Stop slaving away as a content creator. Focus on the music, not the content.
                </p>
                <div className="flex items-center gap-2 text-[#707070] group-hover:text-[#22d3ee] transition-colors text-sm">
                  <span>deadair.us</span>
                  <ExternalLink className="w-3 h-3" />
                </div>
              </div>
            </a>

          </div>

          {/* See All Tools Button */}
          <div className="text-center mt-8">
            <Button
              onClick={() => window.location.href = '/tools'}
              className="neuro-base active:neuro-pressed rounded-3xl px-8 py-4 text-lg smooth-transition hover:scale-105 border border-[#404040]"
            >
              <span className="text-[#b0b0b0]">Explore All Tools</span>
            </Button>
          </div>
        </div>

        {/* BUILT FOR UNDERGROUND */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12 border-2 border-[#606060]/30 bg-gradient-to-br from-[#0a0a0a] to-[#0f0f0f]">
          <div className="text-center">
            <h2 className="text-2xl md:text-4xl text-[#d0d0d0] mb-6 font-masking-renta">
              Built for Underground Artists – Real Results, No Fake Engagement
            </h2>
            
            <p className="text-base md:text-lg text-[#909090] max-w-3xl mx-auto mb-8 leading-relaxed">
              Soundope celebrates experimental and niche sounds. Whether you're dropping hyperpop bangers or blackgaze vibes, 
              our community provides the music promotion for artists, artist feedback, and playlist submission platform you need to break through.
            </p>

            {/* Key Benefits */}
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              <div className="neuro-pressed rounded-2xl p-4 border border-[#90b090]/20">
                <Gift className="w-8 h-8 text-[#90b090] mx-auto mb-2" />
                <p className="text-lg text-[#90b090] font-medium">First Track FREE</p>
              </div>
              <div className="neuro-pressed rounded-2xl p-4 border border-[#c0a060]/20">
                <MessageSquare className="w-8 h-8 text-[#c0a060] mx-auto mb-2" />
                <p className="text-lg text-[#c0a060] font-medium">Feedback Forever</p>
              </div>
              <div className="neuro-pressed rounded-2xl p-4 border border-[#9090b0]/20">
                <Target className="w-8 h-8 text-[#9090b0] mx-auto mb-2" />
                <p className="text-lg text-[#9090b0] font-medium">Organic Growth Guaranteed</p>
              </div>
            </div>

            <p className="text-xl text-[#b0b0b0] mb-8">
              Join thousands of independent musicians getting real listener growth today.
            </p>

            {/* CTA BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={handleGetStarted}
                className="neuro-base active:neuro-pressed rounded-3xl px-8 py-5 text-lg smooth-transition hover:scale-105 border-2 border-[#90b090]/50"
              >
                <span className="text-[#90b090]">Get Started Free</span>
              </Button>
              
              <Button
                onClick={handleUpload}
                className="neuro-base active:neuro-pressed rounded-3xl px-8 py-5 text-lg smooth-transition hover:scale-105 border-2 border-[#9090b0]/50"
              >
                <span className="text-[#9090b0]">Submit Music Free</span>
              </Button>
              
              <Button
                onClick={handleGrowth}
                className="neuro-base active:neuro-pressed rounded-3xl px-8 py-5 text-lg smooth-transition hover:scale-105 border-2 border-[#c0a060]/50"
              >
                <span className="text-[#c0a060]">Learn About PRO Growth Packages</span>
              </Button>
            </div>
          </div>
        </div>

        {/* FOOTER TAGLINE */}
        <div className="text-center">
          <p className="text-xl md:text-2xl text-[#808080] font-masking-renta">
            Soundope – Real Music Feedback & Organic Listener Growth for Independent Artists.
          </p>
        </div>

      </div>
    </div>
  );
}
